import { isNil } from 'lodash';
import { CSSProperties, useCallback, useContext } from 'react';
import { Text } from '@shared/components/Text';
import { ButtonView, View } from '@shared/components/View';
import { InputBlockMedia } from '@shared/components/blocks/lib/inputFile/InputBlockMedia';
import { useFileUpload } from '@shared/components/blocks/lib/inputFile/hooks';
import { FileUpload } from '@shared/components/blocks/types';
import { MBIconV2 } from '@shared/components/image';
import { AuditCheckboxField } from '@shared/data/entity/fields/AuditCheckboxField';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, Colors, getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { AuditFieldFooterActions } from './AuditFieldFooterActions';
import { EditFieldContainer } from './EditFieldContainer';
import styles from './AuditCheckboxEditField.module.css';

interface Props {
  field: AuditCheckboxField | null;
  component: API.AuditCheckboxComponentFragment;
  updateField?: (componentId: string, field: AuditCheckboxField | null) => void;
  error?: string;
  style?: CSSProperties;
}

export function AuditCheckboxEditField({ field, component, updateField, error, style }: Props) {
  const colors = useContext(ColorThemeContext);
  const anchorName = getAnchorName('audit-checkbox-edit-field', component.id);
  const isReadOnly = component.readonly || isNil(updateField);

  const checked = field?.checked ?? false;

  const handleToggle = useCallback(() => {
    updateField?.(
      component.id,
      new AuditCheckboxField({
        checked: !checked,
        files: field?.files ?? [],
        notes: field?.notes ?? '',
      }),
    );
  }, [component.id, updateField, checked, field]);

  const onNotesChange = useCallback(
    (updatedNotes: string) => {
      updateField?.(
        component.id,
        new AuditCheckboxField({ checked, files: field?.files ?? [], notes: updatedNotes }),
      );
    },
    [component.id, updateField, checked, field],
  );

  const onFileAdded = useCallback(
    (file: FileUpload) => {
      updateField?.(
        component.id,
        new AuditCheckboxField({
          checked,
          files: [...(field?.files ?? []), file],
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, checked, field],
  );

  const onFileRemoved = useCallback(
    (file: FileUpload) => {
      updateField?.(
        component.id,
        new AuditCheckboxField({
          checked,
          files: (field?.files ?? []).filter(f => f.url !== file.url),
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, checked, field],
  );

  const { showFilePicker, uploading } = useFileUpload({ onFileAdded, onFileRemoved });

  const checkboxText = getComponentLabel(component);

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showIcon={false}
      showLabel={false}
      error={error}
      style={style}
      notes={
        !!field?.notes && (
          <Text size="body2" color={colors.foregroundActive}>
            {field.notes}
          </Text>
        )
      }
      files={
        (field?.files ?? []).length > 0 && (
          <InputBlockMedia
            files={field?.files ?? []}
            maxWidth={200}
            maxHeight={133}
            minWidth={100}
            compactFileView={false}
            editable={!isReadOnly}
            onRemoved={onFileRemoved}
          />
        )
      }
      footer={
        !isReadOnly && (
          <AuditFieldFooterActions
            showFilePicker={showFilePicker}
            uploading={uploading}
            notes={field?.notes ?? ''}
            onNotesChange={onNotesChange}
          />
        )
      }
    >
      <ButtonView
        onClick={handleToggle}
        disabled={isReadOnly}
        className={styles.toggleRow}
        center
        row
      >
        <View className={styles.toggleIcon}>
          {checked && <MBIconV2 name="check-filled" color={colors.accent} size={grid(4)} />}
          {!checked && (
            <MBIconV2
              name="check-task"
              color={!isNil(error) ? Colors.v2.gray30 : colors.input.borderInactive}
              size={grid(4)}
            />
          )}
        </View>
        <View flex>
          <Text size="body1" color={colors.foregroundActive}>
            {checkboxText}
          </Text>
        </View>
      </ButtonView>
    </EditFieldContainer>
  );
}
